import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { CountryCityFields } from '@/components/shared/country-city-fields';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select, Separator, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { ApiRequestError } from '@/lib/api';
import { CURRENCIES } from '@/lib/currencies';
import { formatMoney } from '@/lib/format';
import { useCompanies, useContacts, useSaveCompany, useSaveContact } from './api';
import { COMPANY_TYPE_LABELS, LIFECYCLE_LABELS } from './labels';
import type { Company, CompanyType, LifecycleStage } from './types';

const TYPES = Object.keys(COMPANY_TYPE_LABELS) as CompanyType[];
const STAGES = Object.keys(LIFECYCLE_LABELS) as LifecycleStage[];

const TYPE_VARIANT: Record<CompanyType, 'default' | 'success' | 'secondary' | 'warning' | 'destructive'> = {
  PROSPECT: 'default',
  CLIENT: 'success',
  PARTNER: 'secondary',
  VENDOR: 'secondary',
  CHURNED: 'destructive',
};

export function CompaniesPage() {
  const [params] = useSearchParams();
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  // Seeded from the link that brought you here, e.g. the dashboard's clients card.
  const [type, setType] = useState(params.get('type') ?? '');
  const [sort, setSort] = useState('');
  const [editing, setEditing] = useState<Company | null>(null);
  const [creating, setCreating] = useState(false);

  const debounced = useDebounce(search);
  const { data, isLoading, error, refetch } = useCompanies({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    type: type || undefined,
    sort: sort || undefined,
  });

  const columns: Column<Company>[] = [
    {
      key: 'name',
      header: 'Company',
      sortKey: 'name',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.industry ?? row.website ?? '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      cell: (row) => <Badge variant={TYPE_VARIANT[row.type]}>{COMPANY_TYPE_LABELS[row.type]}</Badge>,
    },
    {
      key: 'lifecycle',
      header: 'Lifecycle',
      sortKey: 'lifecycle_stage',
      className: 'text-sm text-muted-foreground',
      cell: (row) => LIFECYCLE_LABELS[row.lifecycleStage],
    },
    {
      key: 'people',
      header: 'People',
      className: 'tabular text-sm',
      cell: (row) => row.contactCount,
    },
    {
      key: 'deals',
      header: 'Deals',
      className: 'tabular text-sm',
      cell: (row) => row.dealCount,
    },
    {
      key: 'terms',
      header: 'Terms',
      className: 'text-sm text-muted-foreground',
      cell: (row) => `${row.paymentTermsDays} days`,
    },
    {
      key: 'credit',
      header: 'Credit limit',
      className: 'tabular text-sm',
      cell: (row) => formatMoney(row.creditLimit, row.currency),
    },
    {
      key: 'owner',
      header: 'Owner',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.owner?.name ?? 'Unassigned',
    },
  ];

  return (
    <div>
      <PageHeader
        title="Companies"
        description="Every account — prospects, clients, partners and vendors."
        actions={
          <Can permission="crm.company.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              New company
            </Button>
          </Can>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search name, website or TRN…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={type}
          onChange={(event) => {
            setType(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[9rem]"
        >
          <option value="">All types</option>
          {TYPES.map((value) => (
            <option key={value} value={value}>
              {COMPANY_TYPE_LABELS[value]}
            </option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load companies.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => setEditing(row)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="No companies yet"
        emptyDescription="Add an account, or convert a lead to create one automatically."
        emptyAction={
          <Can permission="crm.company.create">
            <Button onClick={() => setCreating(true)}>
              <Building2 className="h-4 w-4" />
              New company
            </Button>
          </Can>
        }
      />

      {(creating || editing) && (
        <CompanyDialog
          company={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/** One person at the company, as the dialog holds them while being typed. */
type PersonDraft = {
  key: string;
  name: string;
  designation: string;
  phone: string;
};

let nextPersonKey = 0;
const blankPerson = (): PersonDraft => ({
  key: `new-${(nextPersonKey += 1)}`,
  name: '',
  designation: '',
  phone: '',
});

function CompanyDialog({ company, onClose }: { company?: Company; onClose: () => void }) {
  const save = useSaveCompany();
  const saveContact = useSaveContact();
  // /users/options, not /users: owning a company does not make you an admin.
  const { data: users } = useUserOptions();
  const [error, setError] = useState<string | null>(null);

  // Who to call there. A company usually has more than one number, and which
  // one you want depends on why you are calling — so each carries the job
  // title next to it rather than being a single "phone" field on the company.
  const { data: existing } = useContacts({ companyId: company?.id, limit: 50 }, Boolean(company?.id));
  const [people, setPeople] = useState<PersonDraft[]>([]);

  const contacts = existing?.data ?? [];

  const patchPerson = (key: string, changes: Partial<PersonDraft>) =>
    setPeople((current) =>
      current.map((person) => (person.key === key ? { ...person, ...changes } : person)),
    );

  const [form, setForm] = useState({
    name: company?.name ?? '',
    legalName: company?.legalName ?? '',
    website: company?.website ?? '',
    industry: company?.industry ?? '',
    size: company?.size ?? '',
    type: company?.type ?? ('PROSPECT' as CompanyType),
    lifecycleStage: company?.lifecycleStage ?? ('LEAD' as LifecycleStage),
    trn: company?.trn ?? '',
    currency: company?.currency ?? 'AED',
    paymentTermsDays: company?.paymentTermsDays ?? 30,
    creditLimit: company?.creditLimit ?? '',
    ownerId: company?.ownerId ?? '',
    currencies: company?.currencies ?? [],
    addressLine1: company?.billingAddress?.line1 ?? '',
    addressCity: company?.billingAddress?.city ?? '',
    addressCountry: company?.billingAddress?.country ?? '',
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const submit = async () => {
    setError(null);
    try {
      const saved = await save.mutateAsync({
        ...(company ? { id: company.id } : {}),
        name: form.name,
        legalName: form.legalName,
        website: form.website,
        industry: form.industry,
        size: form.size,
        type: form.type,
        lifecycleStage: form.lifecycleStage,
        trn: form.trn,
        currency: form.currency,
        currencies: form.currencies,
        paymentTermsDays: Number(form.paymentTermsDays),
        creditLimit: form.creditLimit || undefined,
        ownerId: form.ownerId,
        billingAddress: {
          line1: form.addressLine1,
          city: form.addressCity,
          country: form.addressCountry,
        },
      });

      // The company has to exist before anybody can belong to it, so the
      // people go in afterwards — and a row with nothing typed in it is not
      // a person, it is an empty row.
      const worth = people.filter(
        (person) => person.name.trim() !== '' || person.phone.trim() !== '',
      );
      for (const [index, person] of worth.entries()) {
        const [firstName, ...rest] = person.name.trim().split(/\s+/);
        await saveContact.mutateAsync({
          companyId: saved.id,
          firstName: firstName || person.phone.trim(),
          lastName: rest.join(' '),
          designation: person.designation.trim(),
          phone: person.phone.trim(),
          // The first one added to a company with nobody in it answers the phone.
          isPrimary: index === 0 && contacts.length === 0,
        });
      }

      onClose();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save the company.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{company ? company.name : 'New company'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Trading name">
            <Input value={form.name} autoFocus onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Legal name">
            <Input value={form.legalName} onChange={(e) => set({ legalName: e.target.value })} />
          </Field>
          <Field label="Website">
            <Input
              value={form.website}
              placeholder="https://"
              onChange={(e) => set({ website: e.target.value })}
            />
          </Field>
          <Field label="Industry">
            <Input value={form.industry} onChange={(e) => set({ industry: e.target.value })} />
          </Field>
          <Field label="Type">
            <Select
              value={form.type}
              onChange={(e) => set({ type: e.target.value as CompanyType })}
            >
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {COMPANY_TYPE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Lifecycle stage">
            <Select
              value={form.lifecycleStage}
              onChange={(e) => set({ lifecycleStage: e.target.value as LifecycleStage })}
            >
              {STAGES.map((value) => (
                <option key={value} value={value}>
                  {LIFECYCLE_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Headcount">
            <Select value={form.size} onChange={(e) => set({ size: e.target.value })}>
              <option value="">Not set</option>
              {['1-10', '11-50', '51-200', '201-500', '500+'].map((band) => (
                <option key={band} value={band}>
                  {band}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tax number (TRN)">
            <Input value={form.trn} onChange={(e) => set({ trn: e.target.value })} />
          </Field>
          <Field label="Currency" className="sm:col-span-2">
            <div className="grid gap-3 sm:grid-cols-[14rem_1fr]">
              <Select
                value={form.currency}
                onChange={(e) =>
                  set({
                    currency: e.target.value,
                    // The default is never also an "other".
                    currencies: form.currencies.filter((code) => code !== e.target.value),
                  })
                }
              >
                {!CURRENCIES.some((currency) => currency.code === form.currency) && (
                  <option value={form.currency}>{form.currency}</option>
                )}
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} — {currency.name}
                  </option>
                ))}
              </Select>

              <div>
                <p className="mb-1 text-xs text-muted-foreground">
                  Also billed in — tick any others this client trades in.
                </p>
                <div className="scrollbar-thin flex max-h-24 flex-wrap gap-x-3 gap-y-1 overflow-y-auto">
                  {CURRENCIES.filter((currency) => currency.code !== form.currency).map(
                    (currency) => (
                      <label
                        key={currency.code}
                        className="flex cursor-pointer items-center gap-1.5 text-xs"
                      >
                        <Checkbox
                          checked={form.currencies.includes(currency.code)}
                          onCheckedChange={() =>
                            set({
                              currencies: form.currencies.includes(currency.code)
                                ? form.currencies.filter((code) => code !== currency.code)
                                : [...form.currencies, currency.code],
                            })
                          }
                        />
                        {currency.code}
                      </label>
                    ),
                  )}
                </div>
              </div>
            </div>
          </Field>
          <Field label="Payment terms (days)">
            <Input
              type="number"
              min={0}
              value={form.paymentTermsDays}
              onChange={(e) => set({ paymentTermsDays: Number(e.target.value) })}
            />
          </Field>
          <Field label="Credit limit">
            <Input
              inputMode="decimal"
              placeholder="0.00"
              value={form.creditLimit}
              onChange={(e) => set({ creditLimit: e.target.value })}
            />
          </Field>
          <Field label="Account owner">
            <Select value={form.ownerId} onChange={(e) => set({ ownerId: e.target.value })}>
              <option value="">Me</option>
              {(users ?? []).map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Billing address" className="sm:col-span-2">
            <Textarea
              rows={2}
              value={form.addressLine1}
              placeholder="Street, building, floor"
              onChange={(e) => set({ addressLine1: e.target.value })}
            />
          </Field>
          <CountryCityFields
            className="sm:col-span-2"
            country={form.addressCountry}
            city={form.addressCity}
            onChange={({ country, city }) =>
              set({
                ...(country !== undefined ? { addressCountry: country } : {}),
                ...(city !== undefined ? { addressCity: city } : {}),
              })
            }
          />
        </div>

        <Separator className="my-4" />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium">Contact numbers</h3>
              <p className="text-xs text-muted-foreground">
                Who to call there, and what they do. Each one becomes a contact under this company.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPeople([...people, blankPerson()])}>
              <Plus className="h-4 w-4" />
              Add number
            </Button>
          </div>

          {contacts.length > 0 && (
            <div className="mb-2 space-y-1">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border bg-muted/30 px-3 py-2 text-sm"
                >
                  <span className="font-medium">{contact.name}</span>
                  {contact.designation && (
                    <span className="text-xs text-muted-foreground">· {contact.designation}</span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground">
                    {contact.phone ?? contact.email ?? 'No number'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {people.map((person) => (
            <div key={person.key} className="mb-2 grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <Input
                value={person.name}
                placeholder="Name"
                onChange={(e) => patchPerson(person.key, { name: e.target.value })}
              />
              <Input
                value={person.designation}
                placeholder="Designation"
                onChange={(e) => patchPerson(person.key, { designation: e.target.value })}
              />
              <Input
                value={person.phone}
                placeholder="Phone"
                onChange={(e) => patchPerson(person.key, { phone: e.target.value })}
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove this number"
                onClick={() => setPeople(people.filter((row) => row.key !== person.key))}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}

          {people.length === 0 && contacts.length === 0 && (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
              No numbers yet. Add as many as you need — reception, the owner, accounts.
            </p>
          )}
        </div>

        <FormError message={error} />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || form.name.trim().length < 2}>
            {save.isPending ? 'Saving…' : company ? 'Save changes' : 'Create company'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
